import { FC, useCallback } from 'react';
import { MdSend as SendIcon } from 'react-icons/md';
import { LoadingButton } from '@mui/lab';
import { Box, Tooltip, Typography } from '@mui/material';
import { Nullable } from 'common-types';
import Panel from '../../elements/Panel/Panel';
import {
  sendTestNotification,
  toggleNotification,
  toggleNotificationForMe,
  updateNotification,
} from '../actions/notification-settings-actions';
import {
  NotificationSettings,
  Recipient,
} from '../types/notification-settings-types';
import RecipientsAutocomplete from './RecipientsAutocomplete';
import { useSnapshot } from 'valtio';
import NotificationConfigureStore from '../stores/notification-configure-store';
import NotificationPanel from './NotificationDisablePanel';
import {
  getNotifyAllTooltip,
  getOrgNotificationButtonTooltip,
  getRecipientsAutocompleteTooltip,
  getTestEmailTooltip,
  getUserNotificationButtonTooltip,
} from '../utils/notification-button-utils';

const EmailNotificationSettings: FC<
  React.PropsWithChildren<EmailNotificationSettingsProps>
> = props => {
  const { isUserAdminRole, notification, potentialRecipients, orgDisplayName } =
    props;
  const { isUserUnsubscribed, enabled, notifyAll } = notification;

  const { isLoadingTestNotification } = useSnapshot(NotificationConfigureStore);

  const onToggleEnableNotification = useCallback(() => {
    toggleNotification(
      notification.notificationChannelType,
      notification.enabled,
    );
  }, [notification]);

  const onToggleEnableNotificationForMe = useCallback(() => {
    toggleNotificationForMe(
      notification.notificationChannelType,
      notification.isUserUnsubscribed,
    );
  }, [notification]);

  const onToggleNotifyAll = useCallback(() => {
    const notifyAll = !notification.notifyAll;
    const recipients = notifyAll ? [] : notification.recipients;
    updateNotification(
      notification.notificationChannelType,
      recipients,
      notifyAll,
    );
  }, [notification]);

  const handleChangeRecipients = useCallback(
    (newRecipients: Recipient[]) => {
      updateNotification(
        notification.notificationChannelType,
        newRecipients,
        notification.notifyAll,
      );
    },
    [notification],
  );

  const onSendTestEmail = useCallback(() => {
    sendTestNotification(notification.notificationChannelType);
  }, [notification]);

  const orgNotificationButtonTooltip = getOrgNotificationButtonTooltip(enabled);
  const userNotificationButtonTooltip = getUserNotificationButtonTooltip(
    isUserUnsubscribed,
    enabled,
  );
  const testEmailTooltip = getTestEmailTooltip(enabled);
  const notifyAllTooltip = getNotifyAllTooltip(
    isUserAdminRole,
    enabled,
    notifyAll,
  );
  const recipientsAutocompleteTooltip = getRecipientsAutocompleteTooltip(
    isUserAdminRole,
    enabled,
    notifyAll,
  );

  return (
    <>
      <Panel p={2} mt={3}>
        <Typography color='primary.main' variant='h6' component='h4'>
          Weekly Email Notifications
        </Typography>
        <NotificationPanel
          toggleTooltip={orgNotificationButtonTooltip}
          isChecked={notification.enabled}
          onChange={onToggleEnableNotification}
          isDisabled={!isUserAdminRole}
          textPrimary='Enable notifications via email'
          textSecondary={`By default all members of your organization (${orgDisplayName}) will receive important updates on your security status via email.`}
        />
        <NotificationPanel
          toggleTooltip={userNotificationButtonTooltip}
          isChecked={!notification.isUserUnsubscribed}
          onChange={onToggleEnableNotificationForMe}
          isDisabled={!notification.enabled}
          textPrimary='Enable notifications for me'
          textSecondary='By default you will receive security update notifications via email.'
        />
        <NotificationPanel
          toggleTooltip={notifyAllTooltip}
          isChecked={!notification.notifyAll}
          onChange={onToggleNotifyAll}
          isDisabled={!isUserAdminRole || !notification.enabled}
          textPrimary='Limit notifications to select members'
          textSecondary='Emails entered below will be the only members who receive notifications.'
          children={
            <Box mt={3}>
              <Tooltip arrow title={recipientsAutocompleteTooltip}>
                <span>
                  <RecipientsAutocomplete
                    isDisabled={
                      !isUserAdminRole ||
                      notification.notifyAll ||
                      !notification.enabled
                    }
                    potentialRecipients={potentialRecipients}
                    recipients={notification.recipients}
                    handleChangeRecipients={handleChangeRecipients}
                  />
                </span>
              </Tooltip>
            </Box>
          }
        />
      </Panel>
      <Box mt={2}>
        <Tooltip arrow title={testEmailTooltip}>
          <span>
            <LoadingButton
              endIcon={<SendIcon />}
              loading={isLoadingTestNotification}
              size='medium'
              variant='outlined'
              color='primary'
              disabled={!notification.enabled}
              onClick={onSendTestEmail}>
              Send me a test email
            </LoadingButton>
          </span>
        </Tooltip>
      </Box>
    </>
  );
};

export interface EmailNotificationSettingsProps {
  isUserAdminRole: boolean;
  notification: NotificationSettings;
  potentialRecipients: Nullable<Recipient[]>;
  orgDisplayName: string;
}

export default EmailNotificationSettings;
